/**
 * Phase 12.2C — human-readable labels for platform audit actions.
 *
 * Design of record: `DASHBOARD_DESIGN.md` v1.1 §5.4.
 *
 * ── Generic by requirement ─────────────────────────────────────────────────
 * §9 constraint 3 requires activity rendering to support new action types
 * WITHOUT a code change. So this is a lookup with a readable fallback, and the
 * activity list uses one generic renderer — never a per-action React component.
 *
 * Every entry below is an action the platform genuinely emits (Phase 12.1C).
 * The v1.0 examples (`platform.company.created`, `platform.company.suspended`,
 * `platform.company.restored`, `platform.user.login`,
 * `platform.subscription.updated`) were removed in v1.1: none of them exists.
 */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  'platform.auth.login.succeeded': 'Platform login',
  'platform.auth.login.failed': 'Failed platform login',
  'platform.auth.login.blocked-locked': 'Platform login blocked',
  'platform.auth.logout': 'Platform logout',
  'platform.auth.refresh.reuse-detected': 'Refresh token reuse detected',
  'platform.auth.password.changed': 'Platform password changed',
  'platform.user.created': 'Platform user created',
  'platform.user.role-changed': 'Platform user role changed',
  'platform.user.activated': 'Platform user activated',
  'platform.user.deactivated': 'Platform user deactivated',
  'platform.user.sessions-revoked': 'Platform user sessions revoked',
  'platform.tenant.provisioned': 'Company provisioned',
  'platform.tenant.registration-updated': 'Company registration updated',
  'platform.email-gateway.created': 'Email gateway created',
  'platform.email-gateway.updated': 'Email gateway updated',
  'platform.email-gateway.deleted': 'Email gateway deleted',
};

/**
 * The labelled action vocabulary, as data.
 *
 * Added in Phase 12.7a so the Audit Logs page's Action filter can be **rendered
 * from this map** rather than hand-typed — `AUDIT_LOGS_DESIGN.md` §5: *"values
 * are never hand-typed into the UI, so the control cannot drift from the
 * backend."*
 *
 * This is an additive export. `ACTION_LABELS` itself is unchanged, so the
 * Dashboard's Recent Activity rendering is byte-for-byte identical — the design
 * is explicit that it "does not alter the dashboard".
 */
export const PLATFORM_AUDIT_ACTION_KEYS: readonly string[] = Object.keys(ACTION_LABELS);

/**
 * Turns `platform.some.new-action` into "Some new action" when it is not in the
 * table, so an action added by a later phase renders legibly with no frontend
 * change. An unrecognised value is never hidden and never mislabelled.
 */
export function platformAuditActionLabel(action: string): string {
  const known = ACTION_LABELS[action];
  if (known !== undefined) return known;

  const withoutPrefix = action.startsWith('platform.') ? action.slice('platform.'.length) : action;
  const words = withoutPrefix.replace(/[.\-_]+/g, ' ').trim();
  if (words.length === 0) return action;

  return words.charAt(0).toUpperCase() + words.slice(1);
}
